import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Smartphone } from "lucide-react";

/**
 * PWA Install Prompt banner.
 *
 * Browsers fire `beforeinstallprompt` when the site is eligible to install.
 * We:
 *   1. Capture the event
 *   2. Render a friendly bottom-sheet on small viewports
 *   3. Re-show the OS prompt when the user taps "Install"
 *   4. Dismiss & remember (localStorage) so users who said "no" are not pestered
 *
 * iOS Safari does NOT fire `beforeinstallprompt` — we instead detect iOS +
 * standalone-state and show a one-time "Tap Share → Add to Home Screen" hint.
 */
const STORAGE_KEY = "tallyx_pwa_install_dismissed_v1";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPwaPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(STORAGE_KEY) === "1") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    if (isIOS()) setShowIosHint(true);

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const onInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice?.outcome === "accepted") localStorage.setItem(STORAGE_KEY, "1");
    setDeferred(null);
  };

  const onDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setDeferred(null);
    setShowIosHint(false);
  };

  if (!deferred && !showIosHint) return null;

  return (
    <div
      className="fixed bottom-3 left-3 right-3 z-50 md:left-auto md:right-4 md:bottom-4 md:max-w-sm bg-white border border-gray-200 rounded-lg shadow-lg p-3 flex items-start gap-3 text-sm"
      data-testid="pwa-install-banner"
    >
      <div className="flex-shrink-0 w-10 h-10 rounded bg-blue-700 grid place-items-center">
        <Smartphone className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-gray-900">Install AXIORA on your device</div>
        {deferred && (
          <p className="text-xs text-gray-600 mt-0.5">
            Add to your home screen for one-tap access and an offline-ready app feel.
          </p>
        )}
        {!deferred && showIosHint && (
          <p className="text-xs text-gray-600 mt-0.5">
            Tap the <strong>Share</strong> icon below, then <strong>Add to Home Screen</strong>.
          </p>
        )}
        <div className="mt-2 flex gap-2">
          {deferred && (
            <Button
              size="sm"
              onClick={onInstall}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="btn-pwa-install"
            >
              Install
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            data-testid="btn-pwa-dismiss"
          >
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="flex-shrink-0 text-gray-400 hover:text-gray-600"
        aria-label="Dismiss install prompt"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
